/**
 * Lightweight toast notifications for storefront actions.
 */

const MAX_VISIBLE = 3;
const DEFAULT_DURATION = 2800;

/** @type {HTMLElement | null} */
let stackEl = null;

function prefersReducedMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function ensureStack() {
	if (stackEl) {
		return stackEl;
	}

	stackEl = document.createElement('div');
	stackEl.id = 'dc-toast-stack';
	stackEl.className = 'dc-toast-stack';
	stackEl.setAttribute('role', 'status');
	stackEl.setAttribute('aria-live', 'polite');
	stackEl.setAttribute('aria-atomic', 'true');
	document.body.appendChild(stackEl);
	return stackEl;
}

function dismissToast(toast) {
	if (!toast || toast.dataset.leaving === 'true') {
		return;
	}

	toast.dataset.leaving = 'true';
	toast.classList.add('is-leaving');

	const remove = () => {
		toast.remove();
	};

	if (prefersReducedMotion()) {
		remove();
		return;
	}

	toast.addEventListener('animationend', remove, { once: true });
	window.setTimeout(remove, 400);
}

/**
 * Show a toast message.
 *
 * @param {string} message
 * @param {{ variant?: 'success' | 'error', duration?: number }} [options]
 */
export function showToast(message, options = {}) {
	const text = String(message || '').trim();
	if (!text) {
		return;
	}

	const variant = options.variant === 'error' ? 'error' : 'success';
	const duration = Number.isFinite(options.duration)
		? Number(options.duration)
		: DEFAULT_DURATION;

	const stack = ensureStack();

	while (stack.children.length >= MAX_VISIBLE) {
		const oldest = stack.firstElementChild;
		if (oldest) {
			dismissToast(oldest);
			oldest.remove();
		} else {
			break;
		}
	}

	const toast = document.createElement('div');
	toast.className = `dc-toast dc-toast--${variant}`;
	toast.textContent = text;
	toast.addEventListener('click', () => dismissToast(toast));

	stack.appendChild(toast);

	window.setTimeout(() => dismissToast(toast), duration);
}

/**
 * Replay WooCommerce notices captured as toasts (no-JS add-to-cart fallback).
 */
export function initNoticeToasts() {
	const el = document.getElementById('dc-wc-notices');
	if (!el) {
		return;
	}

	let items = [];
	try {
		items = JSON.parse(el.textContent || '[]');
	} catch {
		el.remove();
		return;
	}

	el.remove();

	if (!Array.isArray(items)) {
		return;
	}

	items.forEach((item) => {
		const message = String(item?.message || '').trim();
		if (!message) {
			return;
		}
		showToast(message, { variant: item.variant === 'error' ? 'error' : 'success' });
	});
}
